import { useState } from "react";
import {
  Car,
  Calendar,
  Users,
  DollarSign,
  User,
  Baby,
  PawPrint,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./Card";
import {
  formatDate,
  formatDateShort,
  daysBetween,
  nightsBetween,
} from "../date-utils";

interface CheckoutSummaryProps {
  /** `null` while the vehicle is still loading and no name is known yet. */
  vehicleName: string | null;
  from: string;
  to: string;
  adults: number;
  children: number;
  infants: number;
  pets: number;
  totalAmount: number;
  isCalculatingRates: boolean;
  dailyRates: any[];
}

export function CheckoutSummary({
  vehicleName,
  from,
  to,
  adults,
  children,
  infants,
  pets,
  totalAmount,
  isCalculatingRates,
  dailyRates,
}: CheckoutSummaryProps) {
  const [showDailyRates, setShowDailyRates] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const calculateRentalDuration = () => {
    if (!from || !to) return "";

    const days = daysBetween(from, to);
    const nights = nightsBetween(from, to);

    if (days === 1) {
      return `${days} day rental (no overnight stay)`;
    } else {
      return `${days} days, ${nights} nights`;
    }
  };

  const getTotalGuests = () => {
    return adults + children + infants;
  };

  return (
    <Card className="sticky top-4">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-lg">
          <span>Reservation Summary</span>
          <Car className="h-5 w-5 text-gray-600" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Vehicle Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Car className="h-4 w-4" />
            Vehicle
          </div>
          {vehicleName ? (
            <p className="text-sm text-gray-900 font-medium bg-blue-50 p-2 rounded">
              {vehicleName}
            </p>
          ) : (
            <div
              className="h-9 bg-blue-50 rounded animate-pulse"
              aria-label="Loading vehicle name"
            />
          )}
        </div>

        {/* Travel Dates Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Calendar className="h-4 w-4" />
            Travel Dates
          </div>
          <div className="space-y-1">
            <p className="text-sm text-gray-900">
              <span className="font-medium">Pick-up:</span> {formatDate(from)}
            </p>
            <p className="text-sm text-gray-900">
              <span className="font-medium">Return:</span> {formatDate(to)}
            </p>
            <div className="bg-blue-50 p-2 rounded">
              <p className="text-sm text-blue-800 font-medium">
                {calculateRentalDuration()}
              </p>
            </div>
          </div>
        </div>

        {/* Travelers Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Users className="h-4 w-4" />
            Travelers
          </div>
          {getTotalGuests() > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-1 text-sm">
                <User className="h-3 w-3 text-blue-600" />
                <span>{adults} Adults</span>
              </div>
              {children > 0 && (
                <div className="flex items-center gap-1 text-sm">
                  <Users className="h-3 w-3 text-green-600" />
                  <span>{children} Children</span>
                </div>
              )}
              {infants > 0 && (
                <div className="flex items-center gap-1 text-sm">
                  <Baby className="h-3 w-3 text-purple-600" />
                  <span>{infants} Infants</span>
                </div>
              )}
              {pets > 0 && (
                <div className="flex items-center gap-1 text-sm">
                  <PawPrint className="h-3 w-3 text-orange-600" />
                  <span>{pets} Pets</span>
                </div>
              )}
              <div className="col-span-2 bg-gray-50 p-2 rounded">
                <p className="text-sm font-medium text-gray-700">
                  Total: {getTotalGuests()} travelers
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">
              No travelers specified
            </p>
          )}
        </div>

        {/* Daily Rates Section */}
        {dailyRates.length > 0 && (
          <div className="space-y-2 border-t pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <DollarSign className="h-4 w-4" />
                Daily Rates
              </div>
              <button
                onClick={() => setShowDailyRates(!showDailyRates)}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                {showDailyRates ? (
                  <>
                    Hide <ChevronUp className="h-3 w-3" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-3 w-3" />
                  </>
                )}
              </button>
            </div>

            {showDailyRates && (
              <div className="max-h-32 overflow-y-auto space-y-1">
                {dailyRates.map((rate, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center text-xs"
                  >
                    <span className="text-gray-600">
                      {formatDateShort(rate.date)}
                    </span>
                    <span className="font-medium">
                      {formatCurrency(
                        parseFloat(rate.quoted_amount || rate.amount),
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Total Section */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 p-3 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-green-900">
              Estimated Total
            </span>
            <div className="text-right">
              {isCalculatingRates ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin h-5 w-5 border-2 border-green-200 border-t-green-600 rounded-full"></div>
                  <span className="text-sm text-green-700">Calculating...</span>
                </div>
              ) : (
                <>
                  <p className="text-xl font-bold text-green-900">
                    {formatCurrency(totalAmount)}
                  </p>
                  <p className="text-xs text-green-700">USD</p>
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
